import React, { useState, useMemo } from "react";
import {
  Plus,
  Pencil,
  Trash,
  CreditCard,
  TrendDown,
  Calendar,
  Receipt,
} from "@phosphor-icons/react";
import { useData } from "../context/DataContext";
import { Transaction } from "../types";
import { formatCurrency } from "../utils/calculations";
import DollarInput from "../components/DollarInput";
import { useModal } from "../hooks/useModal";

interface ExpenseCategory {
  id: string;
  name: string;
  color: string;
  icon: React.ReactNode;
}

// Extracted components for better organization
const ExpenseSummaryCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  ariaLabel?: string;
}> = ({ title, value, icon, ariaLabel }) => (
  <div className="card" role="region" aria-label={ariaLabel}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">
          {value}
        </p>
      </div>
      <div aria-hidden="true">{icon}</div>
    </div>
  </div>
);

const EmptyState: React.FC<{ onAddClick: () => void }> = ({ onAddClick }) => (
  <div className="card" role="region" aria-label="No expenses">
    <div className="text-center py-12">
      <Receipt
        className="w-12 h-12 text-gray-500 mx-auto mb-4"
        weight="duotone"
        aria-hidden="true"
      />
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        No Expenses
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Add your first expense to start tracking your spending.
      </p>
      <button
        onClick={onAddClick}
        className="btn-primary"
        aria-label="Add your first expense">
        Add Your First Expense
      </button>
    </div>
  </div>
);

const ExpenseItem: React.FC<{
  transaction: Transaction;
  onEdit: (transaction: Transaction) => void;
  onDelete: (id: string) => void;
}> = ({ transaction, onEdit, onDelete }) => (
  <div
    className="flex items-center justify-between p-4 bg-gray-100 dark:bg-gray-700 rounded-lg"
    role="listitem">
    <div className="flex items-center space-x-3">
      <div
        className="w-3 h-3 bg-gradient-to-br from-red-400 to-red-600 rounded-full"
        aria-label="Expense indicator"
      />
      <div>
        <h4 className="text-gray-900 dark:text-white font-medium">
          {transaction.description}
          {transaction.description === "Credit Card Balances" && (
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
              (from credit cards)
            </span>
          )}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {new Date(transaction.date).toLocaleDateString()}
        </p>
      </div>
    </div>
    <div className="flex items-center space-x-2">
      <p className="text-red-600 dark:text-red-400 font-medium">
        -{formatCurrency(transaction.amount)}
      </p>
      {transaction.description !== "Credit Card Balances" && (
        <div
          className="flex items-center space-x-2"
          role="group"
          aria-label={`Actions for ${transaction.description}`}>
          <button
            onClick={() => onEdit(transaction)}
            className="btn-icon"
            aria-label={`Edit ${transaction.description}`}>
            <Pencil
              className="w-4 h-4 text-gray-500 dark:text-gray-400"
              weight="duotone"
            />
          </button>
          <button
            onClick={() => onDelete(transaction.id)}
            className="btn-icon"
            aria-label={`Delete ${transaction.description}`}>
            <Trash
              className="w-4 h-4 text-red-500 dark:text-red-400"
              weight="duotone"
            />
          </button>
        </div>
      )}
    </div>
  </div>
);

const ExpenseModal: React.FC<{
  show: boolean;
  editingExpense: Transaction | null;
  formData: any;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onInputChange: (field: string, value: any) => void;
  onAmountChange: (value: number) => void;
}> = ({
  show,
  editingExpense,
  formData,
  onClose,
  onSubmit,
  onInputChange,
  onAmountChange,
}) => {
  const modalRef = useModal({ isOpen: show, onClose });

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="expense-modal-title">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2
          id="expense-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {editingExpense ? "Edit Expense" : "Add Expense"}
        </h2>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="expense-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Expense Name
            </label>
            <input
              id="expense-name"
              type="text"
              value={formData.name}
              onChange={(e) => onInputChange("name", e.target.value)}
              className="input w-full"
              placeholder="e.g., Rent, Groceries, Gas"
              required
              aria-describedby="expense-name-help"
            />
            <div id="expense-name-help" className="sr-only">
              Enter a descriptive name for this expense
            </div>
          </div>

          <div>
            <label
              htmlFor="expense-category"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              id="expense-category"
              value={formData.category}
              onChange={(e) => onInputChange("category", e.target.value)}
              className="select w-full">
              <option value="Housing">Housing</option>
              <option value="Groceries">Groceries</option>
              <option value="Transportation">Transportation</option>
              <option value="Utilities">Utilities</option>
              <option value="Entertainment">Entertainment</option>
              <option value="Medical">Medical</option>
              <option value="Debt">Debt</option>
              <option value="Insurance">Insurance</option>
              <option value="Subscriptions">Subscriptions</option>
              <option value="Personal">Personal</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <DollarInput
            value={formData.amount}
            onChange={onAmountChange}
            placeholder="$0.00"
            label="Amount"
            required
            id="expense-amount"
            aria-describedby="expense-amount-help"
          />
          <div id="expense-amount-help" className="sr-only">
            Enter the amount in dollars
          </div>

          <div>
            <label
              htmlFor="expense-date"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date
            </label>
            <input
              id="expense-date"
              type="date"
              value={formData.date}
              onChange={(e) => onInputChange("date", e.target.value)}
              className="input w-full"
              required
            />
          </div>

          <div>
            <label
              htmlFor="expense-description"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description (Optional)
            </label>
            <textarea
              id="expense-description"
              value={formData.description}
              onChange={(e) => onInputChange("description", e.target.value)}
              className="input w-full"
              rows={3}
              placeholder="Additional details about this expense"
              aria-describedby="expense-description-help"
            />
            <div id="expense-description-help" className="sr-only">
              Optional additional information about this expense
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
              aria-label={editingExpense ? "Update expense" : "Add expense"}>
              {editingExpense ? "Update Expense" : "Add Expense"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Expenses: React.FC = () => {
  const { getDashboardData, dispatch, getCurrentUser } = useData();
  const dashboardData = getDashboardData();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Transaction | null>(
    null
  );
  const [formData, setFormData] = useState({
    name: "",
    amount: 0,
    category: "Housing",
    description: "",
    date: new Date().toISOString().split("T")[0],
  });

  // Memoize calculations to avoid unnecessary re-renders
  const { allExpenseTransactions, expensesByCategory } = useMemo(() => {
    if (!dashboardData) {
      return {
        allExpenseTransactions: [],
        expensesByCategory: {},
      };
    }

    // Get expense transactions (excluding debt)
    const expenseTransactions = dashboardData.recentTransactions.filter(
      (t: Transaction) => t.type === "expense" && t.category !== "Debt"
    );

    // Use only regular expense transactions (no debt included)
    const allExpenseTransactions = expenseTransactions;

    // Group expenses by category
    const expensesByCategory: Record<string, Transaction[]> =
      allExpenseTransactions.reduce(
        (acc: Record<string, Transaction[]>, transaction: Transaction) => {
          if (!acc[transaction.category]) {
            acc[transaction.category] = [];
          }
          acc[transaction.category].push(transaction);
          return acc;
        },
        {} as Record<string, Transaction[]>
      );

    return {
      allExpenseTransactions,
      expensesByCategory,
    };
  }, [dashboardData]);

  if (!dashboardData) {
    return (
      <main className="space-y-6" role="main" aria-label="Expenses page">
        <div className="card">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Expenses
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Add your first expense to get started.
          </p>
          <button className="btn-primary mt-4" aria-label="Add expense">
            Add Expense
          </button>
        </div>
      </main>
    );
  }

  const expenseCategories: ExpenseCategory[] = [
    {
      id: "housing",
      name: "Housing",
      color: "text-blue-500",
      icon: <CreditCard className="w-5 h-5 text-blue-500" weight="duotone" />,
    },
    {
      id: "groceries",
      name: "Groceries",
      color: "text-green-500",
      icon: <Receipt className="w-5 h-5 text-green-500" weight="duotone" />,
    },
    {
      id: "transportation",
      name: "Transportation",
      color: "text-yellow-500",
      icon: <TrendDown className="w-5 h-5 text-yellow-500" weight="duotone" />,
    },
    {
      id: "utilities",
      name: "Utilities",
      color: "text-purple-500",
      icon: <CreditCard className="w-5 h-5 text-purple-500" weight="duotone" />,
    },
    {
      id: "entertainment",
      name: "Entertainment",
      color: "text-pink-500",
      icon: <Receipt className="w-5 h-5 text-pink-500" weight="duotone" />,
    },
    {
      id: "medical",
      name: "Medical",
      color: "text-red-500",
      icon: <CreditCard className="w-5 h-5 text-red-500" weight="duotone" />,
    },
    {
      id: "debt",
      name: "Debt",
      color: "text-orange-500",
      icon: <TrendDown className="w-5 h-5 text-orange-500" weight="duotone" />,
    },
    {
      id: "insurance",
      name: "Insurance",
      color: "text-indigo-500",
      icon: <CreditCard className="w-5 h-5 text-indigo-500" weight="duotone" />,
    },
    {
      id: "subscriptions",
      name: "Subscriptions",
      color: "text-cyan-500",
      icon: <Receipt className="w-5 h-5 text-cyan-500" weight="duotone" />,
    },
    {
      id: "personal",
      name: "Personal",
      color: "text-emerald-500",
      icon: <Receipt className="w-5 h-5 text-emerald-500" weight="duotone" />,
    },
    {
      id: "other",
      name: "Other",
      color: "text-gray-500",
      icon: <Receipt className="w-5 h-5 text-gray-500" weight="duotone" />,
    },
  ];

  const getCategoryIcon = (category: string) => {
    const foundCategory = expenseCategories.find(
      (cat) => cat.name.toLowerCase() === category.toLowerCase()
    );
    return foundCategory ? (
      foundCategory.icon
    ) : (
      <Receipt className="w-5 h-5 text-gray-500" weight="duotone" />
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const now = new Date();

    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error("No current user found");
      return;
    }

    const transaction: Transaction = {
      id: editingExpense?.id || `expense-${Date.now()}`,
      userId: currentUser.id,
      type: "expense",
      category: formData.category,
      amount: formData.amount,
      description: formData.description || formData.name,
      date: formData.date,
      createdAt: now.toISOString(),
    };

    if (editingExpense) {
      dispatch({ type: "UPDATE_TRANSACTION", payload: transaction });
    } else {
      dispatch({ type: "ADD_TRANSACTION", payload: transaction });
    }

    setShowAddModal(false);
    setEditingExpense(null);
    setFormData({
      name: "",
      amount: 0,
      category: "Housing",
      description: "",
      date: new Date().toISOString().split("T")[0],
    });
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingExpense(transaction);
    setFormData({
      name: transaction.description || transaction.category,
      amount: transaction.amount,
      category: transaction.category,
      description: transaction.description,
      date: transaction.date,
    });
    setShowAddModal(true);
  };

  const handleDelete = (transactionId: string) => {
    if (window.confirm("Are you sure you want to delete this expense?")) {
      dispatch({ type: "DELETE_TRANSACTION", payload: transactionId });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAmountChange = (value: number) => {
    setFormData({ ...formData, amount: value });
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditingExpense(null);
    setFormData({
      name: "",
      amount: 0,
      category: "Housing",
      description: "",
      date: new Date().toISOString().split("T")[0],
    });
  };

  return (
    <main className="space-y-6" role="main" aria-label="Expenses page">
      <div className="flex items-center justify-between">
        <div></div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center space-x-1 sm:space-x-2"
          aria-label="Add new expense">
          <Plus className="w-4 h-4" aria-hidden="true" />
          <span className="hidden sm:inline">Add Expense</span>
          <span className="sm:hidden">Expense</span>
        </button>
      </div>

      {/* Summary Cards */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6"
        aria-label="Expense summary">
        <ExpenseSummaryCard
          title="Monthly Expenses"
          value={formatCurrency(dashboardData.monthlyExpenses)}
          icon={
            <CreditCard className="w-8 h-8 text-red-500" weight="duotone" />
          }
          ariaLabel="Monthly expenses summary"
        />
        <ExpenseSummaryCard
          title="Expense Categories"
          value={Object.keys(expensesByCategory).length}
          icon={
            <TrendDown className="w-8 h-8 text-orange-500" weight="duotone" />
          }
          ariaLabel="Number of expense categories"
        />
        <ExpenseSummaryCard
          title="This Month"
          value={`${allExpenseTransactions.length} transactions`}
          icon={<Calendar className="w-8 h-8 text-blue-500" weight="duotone" />}
          ariaLabel="Number of transactions this month"
        />
      </section>

      {/* Expenses by Category */}
      {Object.keys(expensesByCategory).length === 0 ? (
        <EmptyState onAddClick={() => setShowAddModal(true)} />
      ) : (
        <section className="space-y-6" aria-label="Expenses by category">
          {Object.entries(expensesByCategory).map(
            ([category, transactions]) => (
              <div
                key={category}
                className="card"
                role="region"
                aria-label={`${category} expenses`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div aria-hidden="true">{getCategoryIcon(category)}</div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {category}
                    </h2>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Total
                    </p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatCurrency(
                        transactions.reduce(
                          (sum: number, t: Transaction) => sum + t.amount,
                          0
                        )
                      )}
                    </p>
                  </div>
                </div>

                <div
                  className="space-y-3"
                  role="list"
                  aria-label={`${category} expenses list`}>
                  {transactions.map((transaction: Transaction) => (
                    <ExpenseItem
                      key={transaction.id}
                      transaction={transaction}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            )
          )}
        </section>
      )}

      {/* Add/Edit Modal */}
      <ExpenseModal
        show={showAddModal}
        editingExpense={editingExpense}
        formData={formData}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        onAmountChange={handleAmountChange}
      />
    </main>
  );
};

export default Expenses;
